import { Brain, ChartBar, ClockCountdown, Repeat, X } from '@phosphor-icons/react';
import { METRIC_KEYS, createEasyFeedback, createProfessionalFeedback, getCognitiveLabels, type CognitiveReport, type MetricKey } from '../../../shared/data/cognitiveEngine';

function polarPoint(value: number, index: number, radius = 82) {
  const angle = ((-90 + index * 72) * Math.PI) / 180;
  const scaled = (radius * value) / 100;
  return { x: 180 + Math.cos(angle) * scaled, y: 145 + Math.sin(angle) * scaled };
}

function pointsAt(value: number, radius: number) {
  return METRIC_KEYS.map((_, index) => {
    const point = polarPoint(value, index, radius);
    return `${point.x},${point.y}`;
  }).join(' ');
}

function RadarGraph({ metrics, labels, language }: { metrics: Record<MetricKey, number>; labels: Record<MetricKey, string>; language: string }) {
  const scorePoints = METRIC_KEYS.map((key, index) => {
    const point = polarPoint(metrics[key], index);
    return `${point.x},${point.y}`;
  }).join(' ');
  return (
    <svg className="cognitive-radar" viewBox="0 0 360 300" role="img" aria-label={language === 'ko' ? '인지학습 다섯 지표 점수 그래프' : 'Five-metric cognitive learning score chart'}>
      {[25, 50, 75, 100].map((level) => (
        <polygon key={level} className="cognitive-radar__grid" points={pointsAt(level, 82)} />
      ))}
      {METRIC_KEYS.map((key, index) => {
        const end = polarPoint(100, index);
        return <line key={key} className="cognitive-radar__axis" x1="180" y1="145" x2={end.x} y2={end.y} />;
      })}
      <polygon className="cognitive-radar__data" points={scorePoints} />
      {METRIC_KEYS.map((key, index) => {
        const dot = polarPoint(metrics[key], index);
        const label = polarPoint(100, index, 118);
        return (
          <g key={key} className="cognitive-radar__label" transform={`translate(${label.x} ${label.y})`}>
            <circle r="17" />
            <text className="cognitive-radar__score" textAnchor="middle" dy="4">{metrics[key]}</text>
            <text className="cognitive-radar__name" textAnchor="middle" y="30">{labels[key]}</text>
            <circle className="cognitive-radar__dot" cx={dot.x - label.x} cy={dot.y - label.y} r="4" />
          </g>
        );
      })}
    </svg>
  );
}

function CognitiveReportView({ title, profileLabel, report, onExit, onReplay, language = 'ko' }: {
  title: string;
  profileLabel: string;
  report: CognitiveReport;
  onExit: () => void;
  onReplay: () => void;
  language?: string;
}) {
  const isKorean = language === 'ko';
  const change = report.comparison;
  const labels = getCognitiveLabels(language);
  const easyFeedback = createEasyFeedback(report.metrics, language);
  const professionalFeedback = createProfessionalFeedback(report.metrics, [report.activityId, report.id], [], language);
  return (
    <main className="cognitive-report cognitive-results-page" data-localized-root>
      <header className="cognitive-result-hero">
        <div className="cognitive-result-icon"><Brain weight="duotone" /></div>
        <div className="cognitive-result-title">
          <span>{isKorean ? '학습 완료' : 'LEARNING COMPLETE'}</span>
          <h1>{title}</h1>
          <p>{isKorean ? `${profileLabel} 기준 · 인지 학습 결과` : `${profileLabel} · Cognitive learning result`}</p>
        </div>
        <div className="cognitive-result-rank">
          <strong>{report.overall}<b>/100</b></strong>
          <small>{isKorean ? '이번 학습 종합 점수' : 'TOTAL SCORE'}</small>
          {change && (
            <em>
              <ClockCountdown weight="bold" />
              {isKorean ? `이전보다 ${labels[change.key]} ${change.change >= 0 ? '+' : ''}${change.change}점` : `${labels[change.key]} ${change.change >= 0 ? '+' : ''}${change.change} vs. last time`}
            </em>
          )}
        </div>
        <button type="button" className="cognitive-result-close" onClick={onExit} aria-label={isKorean ? '인지 활동 목록' : 'Activity list'}>
          <X weight="bold" />
        </button>
      </header>
      <section className="cognitive-result-metrics" aria-label={isKorean ? '인지 점수 요약' : 'Cognitive score summary'}>
        {METRIC_KEYS.map((key) => (
          <div key={key}>
            <span>{labels[key]}</span>
            <strong>{report.metrics[key]}</strong>
            <i><b style={{ width: `${report.metrics[key]}%` }} /></i>
          </div>
        ))}
      </section>
      <section className="cognitive-result-body">
        <article className="cognitive-chart-card">
          <div className="cognitive-result-section-title">
            <ChartBar weight="duotone" />
            <div>
              <span>{isKorean ? '인지 프로필' : 'COGNITIVE PROFILE'}</span>
              <h2>{isKorean ? '나의 인지학습 그래프' : 'My cognitive learning graph'}</h2>
            </div>
          </div>
          <RadarGraph metrics={report.metrics} labels={labels} language={language} />
        </article>
        <div className="cognitive-feedback-column">
          <article className="cognitive-user-feedback">
            <span>{isKorean ? '나를 위한 쉬운 안내' : 'EASY GUIDE'}</span>
            <h2>{easyFeedback.title}</h2>
            <p>{easyFeedback.body}</p>
            <ul>
              <li>{easyFeedback.strength}</li>
              <li>{easyFeedback.support}</li>
            </ul>
          </article>
          <article className="cognitive-professional-feedback">
            <span>{isKorean ? '전문가용 상세 소견' : 'PROFESSIONAL INTERPRETATION'}</span>
            <h2>{isKorean ? '수행 기록을 바탕으로 한 분석' : 'Performance-log analysis'}</h2>
            <p>{professionalFeedback.narrative}</p>
          </article>
        </div>
      </section>
      <footer className="cognitive-result-actions">
        <button type="button" onClick={onExit}><X weight="bold" />{isKorean ? '인지 활동 목록' : 'Activity list'}</button>
        <button type="button" className="primary" onClick={onReplay}><Repeat weight="bold" />{isKorean ? '다시 학습' : 'Learn again'}</button>
      </footer>
    </main>
  );
}

export default CognitiveReportView;
